<script lang="ts">
	import { t } from '$i18n/i18n'
	import { inputFieldFormats } from '$utils/formatters'
	import AutocompleteInputField from '$lib/autocomplete/AutocompleteInputField.svelte'
	import InputField from '$lib/molecules/InputField.svelte'
	import Button from '$lib/atoms/Button.svelte'
	import { DefaultRobotoFontProps, type FontProps } from '$types/fontProps'
	import type { Material } from './types/material'
	import {
		type Declares,
		SubstituteMaterialMapping,
		type SubstituteScriptImportData
	} from './types/substitutes'
	import { AutocompleteConfig, type AutocompleteFieldConfig } from '$types/autocompleteFieldConfig'
	import { type MaterialProperty } from './types/materialProperty'
	import { mmpvState } from './mmpv.state.svelte'
	import { onMount, untrack } from 'svelte'
	import {
		concatProperty,
		getStringNameOfPropertyType,
		getSuffix,
		replaceSpecialCharacters
	} from './util/materialSubstituteUtil'

	const LOT_FORM_ERROR_PATH_PREFIX = 'lot.'
	const fontProps: FontProps = DefaultRobotoFontProps('0.875rem', '0.75rem')

	interface Props {
		// undefined until the parent modal has a material selected (see onMount fallback below)
		materialReference: Material | undefined
		materialCandidate: Material | undefined
		data: SubstituteScriptImportData
		onRemoveImport?: (identifier: number) => void
	}

	let { materialReference, materialCandidate, data = $bindable(), onRemoveImport }: Props = $props()

	const materialSubstituteConfig: AutocompleteFieldConfig = AutocompleteConfig('id', 'translation')
	const materialPropertyConfig: AutocompleteFieldConfig = AutocompleteConfig(
		'concattedId',
		'deepName'
	)

	let allMappedProperties: MaterialProperty[] = []
	let materialPropertyOptions: MaterialProperty[] = $state([])
	let declares: Declares[] = $state([])
	let substitutePropertyId: string | undefined = $state()
	/**
	 * we save the concatenated property id in the autocomplete component, an example value is like this:
	 * plQIMLQzo97JDdvmEVzsnv8s3WA.YZFNmSXFJEa93UvgsZ6QODX0hwE.NcE9pYEMr1BDyJlK4O5yEt0G-dE.wQCOuUq8J-FXEOf2_qdjyAnHT_g
	 * A property can exist several times, depending on which parent properties are attached to the material.
	 * The properties are therefore concatenated with the ids of the parent properties
	 */
	let concatenateSubstitutePropertyId: string | undefined = $state()

	const materialSubstituteOptions: {
		translation: string
		id: string
	}[] = Object.keys(SubstituteMaterialMapping).map((mapping) => {
		return {
			id: mapping,
			translation:
				SubstituteMaterialMapping.REFERENCE_MATERIAL.toString() === mapping
					? 'Reference'
					: 'Candidate'
		}
	})

	onMount(() => {
		if (!materialReference) {
			data.materialType = SubstituteMaterialMapping.CANDIDATE_MATERIAL
		}
		handleMaterialSubstituteChanged()
	})

	/**
	 * Removes an import card based on the provided identifier.
	 *
	 * Triggers the `onRemoveImport` callback of MaterialSubstituteModal with the necessary identifier data.
	 * @see {@link handleRemoveImport}
	 */
	function removeImport() {
		onRemoveImport?.(data.identifier)
	}

	/**
	 * Handles the event when a material substitute property is changed. This method
	 * clears the `declares`, resets the `concatenateSubstitutePropertyId`, updates the material property options,
	 * and clears the `defaultPropertyName` and `ownPropertyName` from the `data` object.
	 */
	function handleMaterialSubstituteChanged() {
		declares = []
		concatenateSubstitutePropertyId = undefined
		updateMaterialPropertyOptions()
		data.defaultPropertyName = undefined
		data.ownPropertyName = undefined
	}

	/**
	 * Updates the materialPropertyOptions Autocomplete component based on the selected material type.
	 * Filters properties depending on whether the material type is REFERENCE_MATERIAL or Candidate_MATERIAL.
	 */
	function updateMaterialPropertyOptions() {
		const material =
			data.materialType === SubstituteMaterialMapping.REFERENCE_MATERIAL
				? materialReference
				: materialCandidate
		// $state.snapshot deproxifies
		const filteredProperties: MaterialProperty[] = $state.snapshot(
			data.properties.filter((prop) => material?.propertyIds.some((id) => id === prop.id))
		)
		flatProperties(filteredProperties)
	}

	/**
	 * Handles the event when a substitute property changes. It updates the default property name and set the own
	 * property name as default. The default property name is used as fallback if the own property name is empty (user input).
	 * The new property names are concatenated with a suffix (_REF or _CAN and a unique digit number) and special
	 * characters are replaced.
	 */
	function onSubstitutePropertyChanged() {
		declares = []
		if (concatenateSubstitutePropertyId) {
			substitutePropertyId = allMappedProperties.find(
				(prop) => prop.concattedId === concatenateSubstitutePropertyId
			)?.id
			data.defaultPropertyName =
				allMappedProperties.find((prop) => prop.id === substitutePropertyId)?.displayName +
				getSuffix(data.materialType)
			data.defaultPropertyName = replaceSpecialCharacters(data.defaultPropertyName)
			data.ownPropertyName = data.defaultPropertyName
		} else {
			data.ownPropertyName = undefined
			data.defaultPropertyName = undefined
		}
	}

	/**
	 * Flattens the given nested properties by concatenating them into a single
	 * list of properties and assigns the result to materialPropertyOptions.
	 * As an example, we have a nested property that looks like this:
	 * Fremdbestandteile | Ja | Fremdbestandteile Anteil in % | &lt;5% | Beschreibung Fremdbestandteile &lt;5%
	 * @param {MaterialProperty[]} properties - The filtered properties to flatten.
	 */
	function flatProperties(properties: MaterialProperty[]) {
		allMappedProperties = []
		properties.forEach((materialProperty) => {
			concatProperty(materialProperty, allMappedProperties, mmpvState.materialProperties)
		})
		materialPropertyOptions = $state.snapshot(allMappedProperties)
	}

	/**
	 * Populates the declares array with objects representing property list elements.
	 * Each object contains an id, a defaultName, and an ownName.
	 * default name is used as fallback if ownName (user input) is empty
	 */
	function addDeclares() {
		mmpvState.materialProperties
			.get(substitutePropertyId ?? '')
			?.propertyListElements.forEach((listElement) => {
				declares.push({
					id: listElement.id,
					defaultName: 'Eigenschaftswert_' + replaceSpecialCharacters(listElement.name),
					ownName: 'Eigenschaftswert_' + replaceSpecialCharacters(listElement.name)
				})
			})
	}

	/**
	 * Constructs a property script by determining the material type and property details, then formatting it properly
	 * according to the data and declarations provided. Updates the script text in the `data.text` property.
	 * The script text of this property is used in parent component
	 * @see {@link buildScriptText}
	 */
	function buildPropertyScript() {
		let materialType

		if (data.materialType === SubstituteMaterialMapping.REFERENCE_MATERIAL) {
			materialType = $t('UI.mmpv.modal.substitute.import.reference.material')
		} else {
			materialType = $t('UI.mmpv.modal.substitute.import.candidate.material')
		}

		const propertyName = data.ownPropertyName ?? replaceSpecialCharacters(data.defaultPropertyName)
		const propertyTypeName = getStringNameOfPropertyType(
			mmpvState.materialProperties.get(substitutePropertyId ?? '')?.type
		)

		// no property selected yet: emit an empty script instead of "import undefined from ... as ...;"
		if (!concatenateSubstitutePropertyId || !propertyTypeName || !propertyName) {
			// only write on change (see note below) to avoid re-firing this effect via the bound `data`
			if (data.text !== '') {
				data.text = ''
			}
			return
		}

		// build script text of material property
		let text = `import ${concatenateSubstitutePropertyId} from ${materialType} as ${propertyTypeName} ${propertyName};\n`

		// build script text of list elements
		declares.forEach((declare) => {
			const declareName = declare.ownName
				? declare.ownName
				: replaceSpecialCharacters(declare.defaultName)
			text += `declare ${declare.id} as ${declareName};\n`
		})

		// only write on change: mutating the bound prop invalidates the legacy parent,
		// which re-fires this effect via `data` — an unconditional write would loop forever
		if (data.text !== text) {
			data.text = text
		}
	}

	/**
	 * Handles the text change event for a property input element.
	 * The event containing the user input string of the property name.
	 * If the user clears all characters the propertyName is set to fallback defaultPropertyName.
	 */
	function handlePropertyTextChange(e: Event) {
		const target = e.target as HTMLInputElement
		const namedProp = replaceSpecialCharacters(target.value)
		if (!namedProp || namedProp === '') {
			data.ownPropertyName = data.defaultPropertyName
		} else if (namedProp) {
			data.ownPropertyName = namedProp
		}
		data = { ...data }
	}

	/**
	 * Handles the text change event for a list property input element.
	 * The event containing the user input string of the property name.
	 * If the user clears all characters the propertyName is set to fallback defaultPropertyName.
	 */
	function handlePropertyListElementTextChange(e: Event, declareObj: Declares) {
		const target = e.target as HTMLInputElement
		const namedPropListEle = replaceSpecialCharacters(target.value)
		if (!namedPropListEle || namedPropListEle === '') {
			declareObj.ownName = declareObj.defaultName
		} else if (namedPropListEle) {
			declareObj.ownName = namedPropListEle
		}
	}

	$effect(() => {
		void data
		untrack(() => buildPropertyScript())
	})
	let enableDeclares: boolean = $derived(
		Boolean(
			concatenateSubstitutePropertyId &&
			(mmpvState.materialProperties.get(substitutePropertyId ?? '')?.propertyListElements.length ??
				0) > 0 &&
			declares.length === 0
		)
	)
	$effect(() => {
		void materialCandidate
		void materialReference
		// untrack: only the two materials are dependencies (legacy `$:` semantics),
		// the deep reads inside must not retrigger this effect
		untrack(() => updateMaterialPropertyOptions())
	})
</script>

<div class="material-substitute-import">
	<span>{$t('UI.mmpv.modal.substitute.import.header')}</span>
	<div>
		<AutocompleteInputField
			bind:value={data.materialType}
			autocompleteConfig={materialSubstituteConfig}
			label={$t('UI.material.autocomplete.ph')}
			options={materialSubstituteOptions}
			errorPath={LOT_FORM_ERROR_PATH_PREFIX + 'material'}
			{fontProps}
			required
			fullWidth
			onChange={handleMaterialSubstituteChanged}
		/>
		<AutocompleteInputField
			bind:value={concatenateSubstitutePropertyId}
			autocompleteConfig={materialPropertyConfig}
			label={$t('UI.material.property.autocomplete.ph')}
			options={materialPropertyOptions}
			errorPath={LOT_FORM_ERROR_PATH_PREFIX + 'material'}
			{fontProps}
			required
			fullWidth
			onChange={onSubstitutePropertyChanged}
		/>
	</div>
	<InputField
		bind:value={data.ownPropertyName}
		placeholder={$t('UI.mmpv.modal.substitute.import.property.naming.ph')}
		format={inputFieldFormats.FULL}
		on:change={handlePropertyTextChange}
	/>
	{#if declares.length > 0 && concatenateSubstitutePropertyId}
		<div class="declares">
			<span>{$t('UI.mmpv.modal.substitute.declare.header')}</span>
			{#each declares as declare (declare.id)}
				<div>
					<div>
						<InputField bind:value={declare.defaultName} format={inputFieldFormats.FULL} readOnly />
						<InputField
							bind:value={declare.ownName}
							format={inputFieldFormats.FULL}
							placeholder={$t('UI.mmpv.modal.substitute.import.declare.naming.ph')}
							on:change={(e) => handlePropertyListElementTextChange(e, declare)}
						/>
					</div>
				</div>
			{/each}
		</div>
	{/if}
	<div class="import-and-declare-buttons">
		<Button variant="secondary" color="red" onclick={removeImport}>
			{$t('UI.mmpv.modal.substitute.button.removeImport')}
		</Button>
		<Button variant="primary" color="green" disabled={!enableDeclares} onclick={addDeclares}>
			{$t('UI.mmpv.modal.substitute.button.addDeclare')}
		</Button>
	</div>
</div>

<style>
	@import '$styles/modals.scss';

	div.material-substitute-import {
		padding: 1rem 1rem 0 1rem;

		& > div {
			display: flex;
			flex-direction: column;
			align-items: normal;
			justify-content: center;
			gap: 14px;

			padding-top: 0.6rem;
			padding-bottom: 1rem;
		}

		& div:last-child {
			display: flex;
			flex-direction: row;
			align-items: normal;
			justify-content: center;
			gap: 16px;
		}

		div.import-and-declare-buttons {
			justify-content: flex-end;
		}

		& > div.declares {
			display: flex;
			flex-direction: column;
			align-items: normal;
			justify-content: center;

			& > div {
				border: 1px solid #d9d9d9;
				margin-top: 0.7rem;
				padding-bottom: 0.7rem;
			}

			& > div > div {
				display: flex;
				flex-direction: row;
				align-items: normal;
				justify-content: center;
				gap: 16px;

				padding-top: 0.5rem;
				width: 99%;
				padding-left: 1%;
			}

			& > span {
				font-family: Roboto, sans-serif;
				line-height: 10px;
				font-weight: 500;
				font-size: 0.75rem;
				color: black;
			}
		}

		& > span:first-child {
			font-family: Roboto, sans-serif;
			line-height: 10px;
			font-weight: 500;
			font-size: 0.75rem;
			color: black;
		}

		:global(.sv-dd-item > .sv-item) {
			overflow-x: auto !important;
			scrollbar-width: none !important;
		}

		:global(.sv-dd-item > .sv-item::-webkit-scrollbar) {
			display: none; /* for Chrome, Safari and Opera */
		}

		:global(.sv-item-content) {
			overflow: visible !important;
		}
	}
</style>
